import React from 'react'
import gql from 'graphql-tag'
import moment from 'moment'
import { ApolloError } from 'apollo-client'
import { isNil, path } from 'ramda'
import { useQuery } from '@/hooks/useQuery'
import { ApolloQueryVariables } from '@/constants/types'
import { useMutation, MutationOptions } from '@/hooks/useMutation'
import { useMutation as useApolloMutation } from '@apollo/react-hooks'
import { momentToString, toMoment } from '@/utils/webHelper'
import env from '@/env'
import message from '@/utils/message'

type WebSetting = {
  fileHash: string
  filePash: string
  id: number
  name: string
  url: string
  language: string
  plan: string
  expiryDate: moment.Moment | string
  expiryDateStr: string | string
}

type ImageProp = {
  hash: string
  path: string
  url: string
}

export type QueryWebResult = { response: { data: WebSetting } }

export const getWebSettingQuery = gql`
  query getWebSetting {
    results @rest(type: "WebSetting", path: "/system/profile") {
      data @type(name: WebSetting) {
        fileHash
        filePash
        id
        name
        url
        defaultLang
        systemModuleType @type(name: "SystemModuleType") {
          desc
          value
        }
        expirationAt
      }
    }
  }
`

export const createWebSettingQuery = gql`
  mutation createWebSetting($input: Alarm) {
    results(input: $input)
      @rest(type: "Alarm", method: "POST", path: "/system/profile") {
      data
      errorMsg
    }
  }
`

export const uploadImageQuery = gql`
  mutation uploadImage($input: Input!) {
    results(input: $input)
      @rest(
        type: "ImageProp"
        path: "/upload/image"
        method: "POST"
        bodySerializer: "form"
      ) {
      data @type(name: "ImageProp") {
        hash
        path
        url
      }
      errorMsg
    }
  }
`

export function useQueryWebSetting(variables?: ApolloQueryVariables) {
  const { data, ...others } = useQuery(getWebSettingQuery, {
    ...variables,
    fetchPolicy: 'cache-first',
  })

  const dataSource = (() => {
    if (isNil(path(['results', 'data'], data))) {
      return {
        fileHash: `${env.publicUrl}/images/app-logo-2.png`,
        filePash: '',
        url: `${env.publicUrl}/images/app-logo-2.png`,
        name: '智慧路燈',
        expiryDate: '',
        expiryDateStr: '',
        language: 'zh-TW',
      } as WebSetting
    }

    const _data = path(['results', 'data'], data) as any

    return {
      ..._data,
      url: _data.url || `${env.publicUrl}/images/app-logo-2.png`,
      name: _data.name || '智慧路燈',
      language: _data.defaultLang,
      expiryDate: toMoment(_data.expirationAt),
      expiryDateStr: momentToString(toMoment(_data.expirationAt), 'YYYY/MM/DD'),
    } as WebSetting
  })()

  return {
    web: dataSource,
    ...others,
  }
}

export function useCreateWebSetting(options: MutationOptions) {
  const { loading, handler } = useMutation(createWebSettingQuery, options)
  const [values, setValues] = React.useState<{
    file?: File | null
    name: string
    defaultLang: string
  }>()

  const { uploading, handleUpload } = useUploadImage({
    onCompleted: ({ data }) => {
      handler({ name: values?.name, fileHash: data.hash })
    },
  })

  const handleAdd = (val: {
    file?: File | null
    name: string
    defaultLang: string
  }) => {
    setValues(val)

    if (val.file) {
      handleUpload({
        variables: { input: { file: val.file } },
      })
      return
    }

    handler({ name: val.name, defaultLang: val.defaultLang })
  }

  return { creating: loading || uploading, handleAdd }
}

export function useUploadImage(options: MutationOptions = {}) {
  const [handleUpload, { loading: uploading }] = useApolloMutation(
    uploadImageQuery,
    {
      onError: (error: ApolloError) => {
        if (options.onError) {
          message({
            content: '照片上傳時發生錯誤',
            type: 'error',
          })
        }
      },
      onCompleted: data => {
        if (options.onCompleted) {
          options.onCompleted({
            data: path(['results', 'data'], data) as ImageProp,
          })
        }
      },
    }
  )

  return {
    uploading,
    handleUpload,
  }
}
